import React, { useMemo } from 'react';

import { LineChart } from '@/components/shared/LineChart';
import { ModalSectionSecondaryText } from '@/components/shared/ModalSectionSecondaryText';
import { TeamSongTags } from '@/components/TeamSongTags';
import { TeamSongThumbs } from '@/components/TeamSongThumbs';
import { classNames } from '@/helpers/classNames';
import { useDarkMode } from '@/hooks/useDarkMode';
import { useTeamSongs } from '@/providers/TeamSongsProvider';
import { linearGradientDef } from '@nivo/core';

import AnalysisCard from '../AnalysisCard/AnalysisCard';
import ExampleChartLabel from '../ExampleChartLabel/ExampleChartLabel';
import { demoLineData } from './demoLineData';
import styles from './EnergyFlow.module.scss';

type LineData = {
	x: number;
	y: number;
}[];

const HIGH_VALUE = 2;
const LOW_VALUE = 1;

const EnergyFlow: React.FC<{
	teamSongIds: string[];
}> = ({ teamSongIds }) => {
	const { findTeamSongsById } = useTeamSongs();
	const setlistTeamSongs = findTeamSongsById(teamSongIds);

	const tags = setlistTeamSongs.map((ts) => ts?.energyTags[0]);

	const flow = useMemo(() => {
		return tags.reduce<{ x: number; y: number }[]>((acc, t, i) => {
			// 0, 2, 4, 6 ...
			const index = i * 4;
			const previous: (typeof acc)[number] | undefined = acc[index - 1];
			switch (t) {
				case 'upbeat':
					return [
						...acc,
						{ x: index + 1, y: HIGH_VALUE },
						{ x: index + 2, y: HIGH_VALUE },
						{ x: index + 3, y: HIGH_VALUE },
						{ x: index + 4, y: HIGH_VALUE },
					];
				case 'building':
					return [
						...acc,
						{ x: index + 1, y: previous?.y || LOW_VALUE },
						{ x: index + 2, y: LOW_VALUE },
						{ x: index + 3, y: HIGH_VALUE },
						{ x: index + 4, y: HIGH_VALUE },
					];
				case 'settling':
					return [
						...acc,
						{ x: index + 1, y: previous?.y || LOW_VALUE },
						{ x: index + 2, y: HIGH_VALUE },
						{ x: index + 3, y: HIGH_VALUE },
						{ x: index + 4, y: LOW_VALUE },
					];
				case 'reflective':
					return [
						...acc,
						{ x: index + 1, y: LOW_VALUE },
						{ x: index + 2, y: LOW_VALUE },
						{ x: index + 3, y: LOW_VALUE },
						{ x: index + 4, y: LOW_VALUE },
					];
				default:
					return [
						...acc,
						{ x: index + 1, y: previous?.y || LOW_VALUE },
						{ x: index + 2, y: LOW_VALUE },
						{ x: index + 3, y: LOW_VALUE },
						{ x: index + 4, y: LOW_VALUE },
					];
			}
		}, []);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [tags.join()]);

	const gridXValues = useMemo(() => {
		const n = teamSongIds.length;
		return teamSongIds.map((_, i) => 1 + (n * 4 - 1) * (2 * i + 1) / (2 * n));
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [teamSongIds.length]);

	return teamSongIds.length > 1 ? (
		<AnalysisCard
			title="Energy"
			responsiveArea={
				<TeamSongThumbs
					style={{ justifyContent: 'space-around' }}
					teamSongIds={teamSongIds}
				/>
			}
		>
			<CustomLineChart lineData={flow} gridXValues={gridXValues} n={teamSongIds.length} />

			<TeamSongTags
				style={{ justifyContent: 'space-around' }}
				teamSongIds={teamSongIds}
				editable
			/>
		</AnalysisCard>
	) : (
		<AnalysisCard
			title="Energy"
			responsiveArea={
				<ModalSectionSecondaryText>
					Add more songs to see the flow of your setlist
				</ModalSectionSecondaryText>
			}
		>
			<div className={styles['wrapper']}>
				<CustomLineChart className="mt-lg" lineData={demoLineData} />
				<ExampleChartLabel />
			</div>
		</AnalysisCard>
	);
};

function CustomLineChart({
	lineData,
	className,
	gridXValues,
	n,
}: {
	lineData: LineData;
	className?: string;
	gridXValues?: number[];
	n?: number;
}) {
	const { isDarkModeOn } = useDarkMode();

	return (
		<LineChart
			curve="basis"
			wrapperClassName={classNames([
				className,
				'mt-sm',
				styles['line-chart'],
			])}
			data={[
				{
					id: 'flow',
					data: lineData,
				},
			]}
			colors={[isDarkModeOn ? '#b5c4ff' : '#1340fd']}
			maxValue={2.15}
			min={0.85}
			enablePoints={false}
			margin={{
				bottom: 10,
				left: 0,
				right: 1,
				top: 5,
			}}
			defs={[
				linearGradientDef('solidFill', [
					{ offset: 0, color: '#b3dfff', opacity: 0.8 },
					{ offset: 100, color: '#b3dfff', opacity: 0.8 },
				]),
			]}
			theme={{
				grid: {
					line: {
						strokeDasharray: '0',
					},
				},
			}}
			enableArea={true}
			areaBaselineValue={0.85}
			fill={[{ match: '*', id: 'solidFill' }]}
			lineWidth={3}
			noAxisBottom
			axisLeft={null}
			isInteractive={false}
			animate={false}
			enableGridY={false}
			enableGridX={gridXValues !== undefined}
			gridXValues={gridXValues}
			xScale={n ? { type: 'linear', min: 1, max: n * 4 } : undefined}
		/>
	);
}

export default EnergyFlow;
